/**
 * FroggySpinner — branded loading indicator using the Froggy logo
 * with a gentle pulse animation. Drop-in replacement for Bootstrap's
 * <Spinner animation="border" /> on customer-facing surfaces.
 *
 * Sizes:
 *   sm  — 24px (inline loading next to text)
 *   md  — 40px (default; in-card loading)
 *   lg  — 64px (full-page / route-level loading)
 *
 * Usage:
 *   <FroggySpinner />                    // md, no label
 *   <FroggySpinner size="lg" />
 *   <FroggySpinner label="Loading frogs" />  // visible caption
 *
 * Accessibility: wraps in role="status" with an aria-label so screen
 * readers announce "Loading" without needing the visible label.
 *
 * (P-09a-3, 2026-05-30.)
 */
import React from 'react';
import PropTypes from 'prop-types';
import '../../styles/froggy-spinner.css';

const SIZE_PX = { sm: 24, md: 40, lg: 64 };

const FroggySpinner = ({ size = 'md', label = '', className = '' }) => {
  const px = SIZE_PX[size] || SIZE_PX.md;
  return (
    <div
      className={`ff-spinner ff-spinner--${size} ${className}`.trim()}
      role="status"
      /* Avoid double-announce: when a visible label is present, let
         it be the announcement. When no visible label, fall back to
         aria-label so SR users hear "Loading". (Adversarial review
         LOW-4, 2026-05-30.) */
      aria-label={label ? undefined : 'Loading'}
    >
      <img
        src="/logos/ff_original_96px.png"
        alt=""
        aria-hidden="true"
        width={px}
        height={px}
        className="ff-spinner__logo"
        style={{ width: px, height: px }}
      />
      {label && <span className="ff-spinner__label">{label}</span>}
    </div>
  );
};

FroggySpinner.propTypes = {
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  label: PropTypes.string,
  className: PropTypes.string,
};

export default FroggySpinner;
